import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getMeeting } from '../services/api';

function JoinPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Check for code in URL query params
  useEffect(() => {
    const urlCode = searchParams.get('code');
    if (urlCode) {
      setCode(urlCode.toUpperCase());
    }
  }, [searchParams]);

  const handleCodeChange = (value: string) => {
    // Auto-format: uppercase, allow letters, numbers, and hyphen
    const formatted = value.toUpperCase().replace(/[^A-Z0-9-]/g, '');
    setCode(formatted);
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!code.trim()) {
      setError('Please enter a meeting code');
      return;
    }

    if (!name.trim()) {
      setError('Please enter your name');
      return;
    }

    setIsLoading(true);

    try {
      const result = await getMeeting(code);

      if (!result.success) {
        setError(result.error || 'Meeting not found');
        setIsLoading(false);
        return;
      }

      if (result.meeting?.status === 'expired' || result.meeting?.status === 'ended') {
        setError('This meeting has ended or expired');
        setIsLoading(false);
        return;
      }

      // Store name for waiting room
      sessionStorage.setItem('patientName', name.trim());

      // Navigate to waiting room
      navigate(`/waiting/${code.toUpperCase()}`);
    } catch (err) {
      console.error('Error validating meeting:', err);
      setError('Unable to connect. Please check your internet connection.');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo / Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-saywell-primary rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-slate-800">Join Video Call</h1>
          <p className="text-slate-600 mt-2">
            Enter the meeting code provided by your care team
          </p>
        </div>

        {/* Join Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg p-6 space-y-4">
          <div>
            <label htmlFor="code" className="block text-sm font-medium text-slate-700 mb-1">
              Meeting Code
            </label>
            <input
              id="code"
              type="text"
              value={code}
              onChange={(e) => handleCodeChange(e.target.value)}
              placeholder="JOHN-1234"
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-saywell-primary focus:border-transparent text-lg text-center tracking-wider font-mono"
              autoComplete="off"
              autoFocus
            />
            <p className="text-xs text-slate-500 mt-1">
              Example: JOHN-1234
            </p>
          </div>

          <div>
            <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-1">
              Your Name
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter your name"
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-saywell-primary focus:border-transparent"
              autoComplete="name"
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-saywell-primary text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Connecting...
              </span>
            ) : (
              'Join Meeting'
            )}
          </button>
        </form>

        {/* Help text */}
        <p className="text-center text-sm text-slate-500 mt-6">
          Having trouble? Contact your care team for assistance.
        </p>
      </div>
    </div>
  );
}

export default JoinPage;
